import { inject } from '@angular/core';
import { of } from 'rxjs';
import {
    CanvasCloseCommand,
    CanvasCommand,
    CanvasStatusCommand,
    collectCanvasCommands,
    getToolStartName,
    Interceptor,
    isToolStartCommand,
    PromptResponse
} from '@panaya/ai-chat/domain';
import { CanvasToolEntry } from '@panaya/ai-chat/shared';
import { CanvasDisplayRegistry } from './canvas-display.registry';
import { CanvasFrameStore } from './canvas-frame.store';
import { CanvasToolRegistry } from './canvas-tool.registry';
import { CanvasTurnTracker } from './canvas-turn.tracker';
import { CANVAS_RESETTABLES } from './canvas-reset.service';
import { AiChatThinkingService } from '../conversation-tools/thinking/ai-chat-thinking.service';

/** The commands the frame owns; `canvas_update` belongs to the content family it names. */
type FrameCommand = CanvasStatusCommand | CanvasCloseCommand;
type FrameCommandHandlers = { [Type in FrameCommand['type']]: (command: Extract<FrameCommand, { type: Type }>) => void };

/**
 * Streaming interceptor for the canvas frame: the `tool_start` of a tool the host registered with
 * `provideCanvasForTool` opens it, `canvas_status` is a plain set, `canvas_close` closes. Runs on every poll, and each
 * poll repeats the whole turn, so the turn tracker makes each registered `tool_start` open at most once.
 * `tool_complete` carries no tool name and is never used to know which tool ended.
 */
export function createCanvasFrameInterceptor(): Interceptor<PromptResponse> {
    const store = inject(CanvasFrameStore);
    const registry = inject(CanvasToolRegistry);
    const displays = inject(CanvasDisplayRegistry);
    const tracker = inject(CanvasTurnTracker);
    const thinking = inject(AiChatThinkingService);
    const resettables = inject(CANVAS_RESETTABLES);

    const openForNewToolStarts = (response: PromptResponse): void => {
        const registeredStarts = response.commands
            .filter(isToolStartCommand)
            .map((command) => registry.find(getToolStartName(command)))
            .filter((entry): entry is CanvasToolEntry => entry !== null);

        registeredStarts.slice(tracker.handledToolStarts()).forEach((entry) => openOnce(entry));

        tracker.markHandled(registeredStarts.length);
    };

    /**
     * A new canvas starts empty: what the previous canvas showed (the draft store keeps its last draft after a close, for
     * the draft card) is cleared, so the thinking state never shows old content. The content's interceptors run after
     * this one, so nothing that arrives in the same poll is cleared.
     */
    const openOnce = (entry: CanvasToolEntry): void => {
        if (store.isOpen()) {
            return;
        }

        // A canvas nothing can draw would stay on its waiting screen with the page hidden behind it, so it never opens.
        if (displays.resolve(entry.display_type) === null) {
            console.warn(`AI Chat canvas: no display is registered for '${entry.display_type}'; ${entry.tool} does not open the canvas.`);

            return;
        }

        resettables.forEach((resettable) => resettable.reset());

        store.open(entry);

        tracker.markOpened();
    };

    const setCanvasStatus = (command: CanvasStatusCommand): void => {
        store.setStatus(command.status, command.message);

        if (command.status === 'creating') {
            thinking.notifyThinkingEvent({
                state: 'thinking',
                translationKey: store.progressKey('creating', 'aichat.canvas.status_line.creating')
            });
        }
    };

    const closeCanvas = (command: CanvasCloseCommand): void => store.close(command.result);

    const handlers: FrameCommandHandlers = {
        canvas_status: setCanvasStatus,
        canvas_close: closeCanvas
    };

    const isFrameCommand = (command: CanvasCommand): command is FrameCommand => command.type in handlers;

    return (response: PromptResponse) => {
        // A tool_start always comes before the commands its run produces, so the open goes first.
        openForNewToolStarts(response);

        collectCanvasCommands(response)
            .filter(isFrameCommand)
            .forEach((command) => dispatch(handlers, command));

        // Repeated on every poll so the canvas text wins over the generic tool texts the thinking interceptors set.
        if (store.isOpen() && store.status() === null) {
            thinking.notifyThinkingEvent({
                state: 'thinking',
                translationKey: store.progressKey('generating', 'aichat.canvas.status_line.generating')
            });
        }

        return of(response);
    };
}

/**
 * Calls the handler for the command's type. TypeScript cannot relate `handlers[command.type]` to the narrowed
 * `command` by itself; `FrameCommandHandlers` guarantees the handler under each key takes that key's command.
 */
function dispatch(handlers: FrameCommandHandlers, command: FrameCommand): void {
    const handler = handlers[command.type] as (frameCommand: FrameCommand) => void;

    handler(command);
}
